/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import WarehouseSyncModel from 'cio/models/warehouse-sync';
import FormValidations from 'cio/utils/form-validations';
import ApiErrorsService from 'cio/services/api-errors';
import AlertsService from 'cio/services/alerts';
import AnalyticsService from 'cio/services/analytics';
import {
  AzureBlobConfigValidations,
  GCSConfigValidations,
  S3ConfigValidations,
  YandexConfigValidations,
} from 'cio/utils/warehouse/validations';
import { getWarehouseDestinationName, WarehouseDestination } from 'cio/utils/warehouse';
import WarehouseSyncConfigurationS3 from 'cio/components/warehouse-sync/configure/s3';
import WarehouseSyncConfigurationGCS from 'cio/components/warehouse-sync/configure/gcs';
import WarehouseSyncConfigurationYandex from 'cio/components/warehouse-sync/configure/yandex';
import WarehouseSyncConfigurationAzureBlob from 'cio/components/warehouse-sync/configure/azure-blob';

interface WarehouseSyncBucketConfigurationArgs {
  model: WarehouseSyncModel;
  destination: WarehouseDestination;
  readOnly: boolean;
}

export default class WarehouseSyncBucketConfiguration extends Component<WarehouseSyncBucketConfigurationArgs> {
  @service alerts!: AlertsService;
  @service analytics!: AnalyticsService;
  @service apiErrors!: ApiErrorsService;

  @tracked isShowingModal = false;
  @tracked validations = FormValidations.create({
    context: this,
    validations: {
      s3: S3ConfigValidations,
      gcs: GCSConfigValidations,
      yandex: YandexConfigValidations,
      azure_blob: AzureBlobConfigValidations,
    }[this.args.destination],
    modelKey: {
      s3: 'args.model.s3Config',
      gcs: 'args.model.gcsConfig',
      yandex: 'args.model.yandexConfig',
      azure_blob: 'args.model.azureConfig',
    }[this.args.destination],
  });

  get config() {
    return {
      s3: this.args.model.s3Config,
      gcs: this.args.model.gcsConfig,
      yandex: this.args.model.yandexConfig,
      azure_blob: this.args.model.azureConfig,
    }[this.args.destination];
  }

  get configurationComponent() {
    return {
      s3: WarehouseSyncConfigurationS3,
      gcs: WarehouseSyncConfigurationGCS,
      yandex: WarehouseSyncConfigurationYandex,
      azure_blob: WarehouseSyncConfigurationAzureBlob,
    }[this.args.destination];
  }

  get hasBucketNameField() {
    return this.config && 'bucketName' in this.config;
  }

  @action
  toggleModal(): void {
    if (this.isShowingModal) {
      this.args.model.rollbackAttributes();
    }

    this.isShowingModal = !this.isShowingModal;

    if (this.isShowingModal) {
      this.analytics.track('warehouse', 'update_configuration_started', { model: this.args.model });
    }
  }

  updateTask = dropTask(async (e: Event) => {
    e.preventDefault();
    this.apiErrors.clear();

    await this.validations.validateModel();

    let { model } = this.args;
    try {
      model.set(
        'updateType',
        {
          s3: 's3_config',
          gcs: 'gcs_config',
          yandex: 'yandex_config',
          azure_blob: 'azure_config',
        }[this.args.destination],
      );

      await model.save();

      this.alerts.success(
        'warehouse.bucket-config',
        `Updated ${getWarehouseDestinationName(this.args.destination)} bucket configuration`,
      );
      this.analytics.track('warehouse', 'update_configuration_completed', { model: this.args.model });
      this.isShowingModal = false;
    } catch (e) {
      this.apiErrors.report(e);
    }
  });
}
